import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

import { InputGroup, InputGroupText, Input } from 'reactstrap';

import Tooltip, {
  tooltipPropTypes,
  getTooltipTarget
} from 'components/Tooltip';

const startingWidth = 155;
const maxWidth = 300;

const OuterSearchWrapper = styled.div`
  z-index: 1;
  margin-right: 8px;
`;

const InputGroupTextWhite = styled(InputGroupText)`
  background: transparent;
  transition: border-color 0.15s ease-in-out;
  border-color: ${props => (props.focused === 'true' ? '#0095FF' : '#ced4da')};
`;
const InputNoLeftBorder = styled(Input)`
  border-left-width: 0;
  border-right-width: ${props => (props.focused === 'true' ? '0' : '1px')};
  -webkit-box-shadow: none !important;
  -moz-box-shadow: none !important;
  box-shadow: none !important;
  border-color: ${props =>
    props.focused === 'true' ? '#0095FF' : '#ced4da'} !important;
`;
const SearchResultsWrapper = styled.div`
  position: absolute;
  background-color: white;
  border-radius: 5px;
  border: 1px solid #767676;
  box-sizing: border-box;
  background: #ffffff;
  padding: 16px;
  width: ${maxWidth}px;
  transform: translateY(4px);
  max-height: calc(100vh - 350px);
  overflow: auto;
`;
export const InputGroupFocusable = styled(InputGroup)`
  transition: all 0.15s ease-in-out;
  width: ${props => (props.focused === 'true' ? maxWidth : startingWidth)}px;
  background: white;

  @media only screen and (max-width: 540px) {
    width: calc(100vw - 60px);
  }
`;
export const SearchResultDiv = styled.div`
  margin-bottom: 5px;
  text-align: left;
  display: flex;
  align-items: center;
  cursor: pointer;
`;
export const SearchResultTextWrapper = styled.div`
  margin-left: 8px;
  color: #333333;
  font-size: 14px;
  line-height: 16px;
  overflow: hidden;
`;
const ElipsedText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const SearchResultTitle = styled(ElipsedText)`
  font-size: 14px;
  line-height: 16px;
  display: block;
  color: #333333;
  min-height: 12px;
`;
export const SearchResultDescription = styled(ElipsedText)`
  font-size: 12px;
  line-height: 12px;
  color: #767676;
  display: block;
  min-height: 12px;
`;

const SearchIcon = ({ focused }) => {
  return (
    <InputGroupTextWhite focused={focused}>
      <FontAwesomeIcon icon={faSearch} />
    </InputGroupTextWhite>
  );
};

export const SearchInputFocusable = ({
  onChange,
  onCancel,
  value,
  tooltipProps
}) => {
  const focused = value ? 'true' : 'false';
  return (
    <>
      <Tooltip {...tooltipProps} />
      <InputGroupFocusable
        focused={focused}
        {...getTooltipTarget(tooltipProps)}
      >
        <SearchIcon focused={focused} />

        <InputNoLeftBorder
          data-testid="searchInput"
          value={value}
          placeholder="Search"
          focused={focused}
          onChange={onChange}
        />
        {focused === 'true' && (
          <InputGroupTextWhite focused={focused} onClick={onCancel}>
            <FontAwesomeIcon
              icon={faTimes}
              aria-labelledby="search-input-close-icon-title"
            />
            <title
              id="search-input-close-icon-title"
              className="visually-hidden"
            >
              close search input
            </title>
          </InputGroupTextWhite>
        )}
      </InputGroupFocusable>
    </>
  );
};

export const SearchResults = ({
  term,
  results,
  onResultClick,
  ResultItem,
  dateFormat
}) => {
  const resultItems = results.map(result => {
    return (
      <ResultItem
        key={`search-event-${result._id}`}
        {...{ term, result, dateFormat, onClick: onResultClick }}
      />
    );
  });

  return (
    <SearchResultsWrapper data-testid="searchResults">
      {resultItems}
    </SearchResultsWrapper>
  );
};

export const SearchInput = ({
  onChange,
  onResultClick,
  onCancel,
  value,
  results,
  ResultItem,
  dateFormat,
  tooltipProps
}) => {
  const hasResults = results && results.length > 0;
  const hasValue = value !== '';

  return (
    <OuterSearchWrapper className="">
      <SearchInputFocusable
        value={value}
        onChange={onChange}
        onCancel={onCancel}
        tooltipProps={tooltipProps}
      />
      {hasResults && hasValue && (
        <SearchResults
          term={value}
          onResultClick={onResultClick}
          results={results}
          ResultItem={ResultItem}
          dateFormat={dateFormat}
        />
      )}
    </OuterSearchWrapper>
  );
};

SearchInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  onResultClick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  value: PropTypes.string,
  results: PropTypes.array,
  dateFormat: PropTypes.string,
  tooltipProps: PropTypes.shape(tooltipPropTypes)
};
